import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, MenuItem, Select, Switch, Tooltip } from '@mui/material';

import { MDTypography, MDBox } from '@components';
import {
  DATE_GROUP,
  DATE_HOUR_GROUP,
  DATE_TIME_GROUP,
  DATE_GROUP_DISPLAY
} from '@constants/common';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CHANGED_DATE_INTERVAL_GROUP,
  REMOVED_DATE_INTERVAL_GROUP
} from '@constants/eventNames';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import { setUserNotifications } from '@components/Notification/slice';
import { RootState } from '@store/store';
import { setChartData } from '@pages/EditChart/slice';
import { Filters } from '@DStypes/chart';
import dayjs from '@utils/dayjs';
import {
  getDateDiff,
  getDateGroupOrderDiff,
  getEndOfDateGroup
} from '@utils/chart';

interface Props {
  setDateGroup: (dateGroup: DATE_GROUP | '') => void;
  dateGroup: DATE_GROUP | '';
  disabled?: boolean;
  isDateTime: boolean;
  filters: Filters | null;
  data: any[];
  xAxisField: string;
}

const ChangeDateGroup: React.FC<Props> = ({
  setDateGroup,
  dateGroup,
  disabled,
  isDateTime,
  filters,
  data,
  xAxisField
}) => {
  const initialTooltip = disabled
    ? 'Only available when a Date column is shown'
    : 'Click to change the date group';

  const [dateGroupToolTip, setDateGroupToolTip] = useState(initialTooltip);
  const [showDateGroupDropdown, setShowDateGroupDropdown] = useState(false);

  const reduxDispatch = useDispatch();
  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );

  const filteredData = useMemo(() => {
    if (data && filters && xAxisField && !disabled)
      if (filters?.xAxisLabelsExcluded?.length)
        return data.filter(
          (item: any) =>
            !filters?.xAxisLabelsExcluded.includes(item[xAxisField])
        );

    return data;
  }, [data, filters]);

  const { startingDate, endingDate } = useMemo(() => {
    if (disabled || !filteredData)
      return { startingDate: null, endingDate: null };

    return {
      startingDate: dayjs(filteredData[0]?.[xAxisField]),
      endingDate: getEndOfDateGroup(
        dateGroup,
        dayjs(filteredData[filteredData.length - 1]?.[xAxisField])
      )
    };
  }, [filteredData]);

  useEffect(() => {
    if (dateGroup && !disabled) setShowDateGroupDropdown(true);
    else setShowDateGroupDropdown(false);
  }, [dateGroup, disabled]);

  const toggleDateInterval = (value: boolean) => {
    setShowDateGroupDropdown(value);
    if (!value) {
      setDateGroup('');
      sendAmplitudeData(REMOVED_DATE_INTERVAL_GROUP);
      const payload: NotificationData = {
        created_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        is_read: false,
        is_silent: false,
        is_user_message: true,
        message_event: MSG_EVENT.TIME_SERIES_DISABLED,
        message_text:
          'We do not recommend a Time Series chart to be shown without an interval selected.',
        updated_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        message_type: MSG_TYPE.INFO,
        message_data: JSON.stringify({
          chart_id: currentChartData.id,
          project: { id: '', name: currentChartData.chart_project_name }
        }),
        is_important: false,
        recipient_obj: 0
      };
      const modifiedChartData = {
        ...currentChartData,
        config: { ...currentChartData.config, date_group: 'none' }
      };

      reduxDispatch(
        setChartData({
          ...modifiedChartData
        })
      );
      reduxDispatch(setUserNotifications(payload));
    }

    setDateGroupToolTip(
      value === false
        ? 'Click to change the date group'
        : 'Click to disable the selected date group'
    );
  };

  return (
    <Grid
      container
      alignItems={'center'}
      justifyContent='space-between'
      height='45px'
    >
      <Grid container item alignItems='center' xs={7}>
        <Tooltip
          title={dateGroupToolTip}
          placement='top'
          sx={{ pointerEvents: 'auto' }}
        >
          <span>
            <Switch
              disabled={disabled}
              checked={!disabled && (showDateGroupDropdown || !!dateGroup)}
              onChange={(e, value) => {
                toggleDateInterval(value);
              }}
            />
          </span>
        </Tooltip>
        <MDTypography
          variant='sm'
          color='info'
          ml={1}
          mr={0.25}
          opacity={disabled ? '0.5' : '1'}
        >
          Date Interval (Group)
        </MDTypography>
      </Grid>
      {showDateGroupDropdown && (
        <Grid item>
          <Select
            sx={{ p: '10px', pl: 0, width: '120px' }}
            color='secondary'
            MenuProps={{
              disablePortal: true,
              sx: { top: '16px', left: '12px' },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'right'
              }
            }}
            value={dateGroup || ''}
            onChange={(e) => {
              sendAmplitudeData(CHANGED_DATE_INTERVAL_GROUP, {
                'Interval Type (before)': dateGroup === '' ? 'None' : dateGroup,
                'Interval Type (after)': e.target.value as DATE_GROUP
              });
              setDateGroup((e.target.value as DATE_GROUP) || '');
            }}
            displayEmpty
            renderValue={(selected: DATE_GROUP | '') => {
              if (selected === '')
                return (
                  <MDBox
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                  >
                    <MDTypography variant='sm' color='grey'>
                      Select here
                    </MDTypography>
                  </MDBox>
                );

              return (
                <MDBox
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <MDTypography
                    variant='sm'
                    color='info'
                    sx={{ textTransform: 'capitalize !important' }}
                  >
                    {DATE_GROUP_DISPLAY[selected]}
                  </MDTypography>
                </MDBox>
              );
            }}
          >
            {Object.values(isDateTime ? DATE_HOUR_GROUP : DATE_GROUP).map(
              (group) => {
                const currentDateGroup =
                  dateGroup ||
                  (isDateTime ? DATE_TIME_GROUP.HOURS : DATE_TIME_GROUP.DAYS);

                const isLargerThanDefault: boolean =
                  getDateGroupOrderDiff(
                    currentChartData?.config?.default_date_group,
                    group
                  ) < 0;

                const isLargerThanCurrent: boolean =
                  getDateGroupOrderDiff(currentDateGroup as any, group) <= 0;

                const isDisabled: boolean = (endingDate &&
                  startingDate &&
                  !isLargerThanDefault &&
                  !isLargerThanCurrent &&
                  getDateDiff(
                    group,
                    startingDate,
                    endingDate,
                    currentDateGroup
                  ) > 500) as boolean;

                return (
                  <MenuItem
                    value={group}
                    key={group}
                    disableRipple={isDisabled}
                    sx={{ p: 0 }}
                  >
                    <Tooltip
                      title={
                        isDisabled
                          ? 'This option is disabled to prevent the chart from becoming difficult to understand. To use this format, please shorten the time period by applying filter from the Filter tab, and then choose this format.'
                          : ''
                      }
                      placement='left'
                      disableInteractive
                    >
                      <MDTypography
                        variant='sm'
                        color='info'
                        textGradient
                        onClick={(e: any) => {
                          if (isDisabled) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        style={{
                          width: '100%',
                          opacity: isDisabled ? '.2' : '1',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          padding: '0.3rem 1rem'
                        }}
                      >
                        {DATE_GROUP_DISPLAY[group as DATE_GROUP]}
                      </MDTypography>
                    </Tooltip>
                  </MenuItem>
                );
              }
            )}
          </Select>
        </Grid>
      )}
    </Grid>
  );
};

export default ChangeDateGroup;
